import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import PropertyCarousel from "@/components/PropertyCarousel";
import { Button } from "@/components/ui/button";
import { Building2, Users, TrendingUp, CheckCircle, Plus, Eye, ArrowUpRight, UserCheck, Crown, Briefcase, User } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Get user role
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user.role === "admin";
  const isSourcingManager = user.role === "sourcing_manager";
  const isAgent = user.role === "agent";
  const isTeamLeader = user.role === "team_leader";

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Refetch when sourcing manager changes context
  useEffect(() => {
    const handler = () => fetchDashboardData();
    window.addEventListener('context-changed', handler);
    return () => window.removeEventListener('context-changed', handler);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/reports/dashboard`, { headers }),
        axios.get(`${API}/leads`, { headers }),
      ]);

      setStats(statsRes.data);
      setRecentLeads(leadsRes.data.slice(0, 6));
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0f2744] border-t-transparent" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="fade-in space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#0f2744] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Dashboard
            </h1>
            <p className="text-slate-500 mt-1">
              Overview of your real estate operations
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => navigate("/properties/new")}
              variant="outline"
              className="border-[#0f2744] text-[#0f2744] hover:bg-[#0f2744]/5 rounded-lg"
              data-testid="add-property-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Property
            </Button>
            <Button
              onClick={() => navigate("/leads/new")}
              className="bg-[#c9a227] hover:bg-[#ae8220] text-white rounded-lg shadow-md hover:shadow-lg transition-all"
              data-testid="add-lead-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Lead
            </Button>
          </div>
        </div>

        {/* Metrics Grid - Bento Style */}
        <div className={`grid grid-cols-1 md:grid-cols-2 ${isAdmin || isTeamLeader ? 'lg:grid-cols-5' : 'lg:grid-cols-3'} gap-6`}>
          {/* Total Properties */}
          <div 
            className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group cursor-pointer"
            onClick={() => navigate("/properties")}
            data-testid="metric-total-properties"
          >
            <div className="absolute left-0 top-0 h-full w-1 bg-[#0f2744]" />
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Properties</p>
                <p className="text-4xl font-bold text-[#0f2744] mt-2">{stats?.total_properties || 0}</p>
                <p className="text-sm text-slate-500 mt-1">Total listed</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-[#0f2744]/10 flex items-center justify-center group-hover:bg-[#0f2744]/20 transition-colors">
                <Building2 className="h-6 w-6 text-[#0f2744]" />
              </div>
            </div>
            <ArrowUpRight className="absolute bottom-4 right-4 h-4 w-4 text-slate-300 group-hover:text-[#0f2744] transition-colors" />
          </div>

          {/* Total Leads */}
          <div 
            className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group cursor-pointer"
            onClick={() => navigate("/leads")}
            data-testid="metric-total-leads"
          >
            <div className="absolute left-0 top-0 h-full w-1 bg-[#c9a227]" />
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Leads</p>
                <p className="text-4xl font-bold text-[#0f2744] mt-2">{stats?.total_leads || 0}</p>
                <p className="text-sm text-slate-500 mt-1">Active inquiries</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-[#c9a227]/10 flex items-center justify-center group-hover:bg-[#c9a227]/20 transition-colors">
                <Users className="h-6 w-6 text-[#c9a227]" />
              </div>
            </div>
            <ArrowUpRight className="absolute bottom-4 right-4 h-4 w-4 text-slate-300 group-hover:text-[#c9a227] transition-colors" />
          </div>

          {/* Conversion Rate */}
          <div 
            className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group cursor-pointer"
            onClick={() => navigate("/reports")}
            data-testid="metric-conversion-rate"
          >
            <div className="absolute left-0 top-0 h-full w-1 bg-emerald-500" />
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Conversion</p>
                <p className="text-4xl font-bold text-[#0f2744] mt-2">{stats?.conversion_rate || 0}%</p>
                <p className="text-sm text-slate-500 mt-1">Success rate</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-emerald-500/10 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
                <TrendingUp className="h-6 w-6 text-emerald-500" />
              </div>
            </div>
            <ArrowUpRight className="absolute bottom-4 right-4 h-4 w-4 text-emerald-500 transition-colors" />
          </div>

          {/* Team Overview - Only visible to Admin and Team Leader */}
          {(isAdmin || isTeamLeader) && (
            <div 
              className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group cursor-pointer"
              onClick={() => navigate("/users")}
              data-testid="metric-total-agents"
            >
              <div className="absolute left-0 top-0 h-full w-1 bg-purple-500" />
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Team</p>
                  <p className="text-4xl font-bold text-[#0f2744] mt-2">{(stats?.total_team_leaders || 0) + (stats?.total_sourcing_managers || 0) + (stats?.total_agents || 0)}</p>
                  <p className="text-sm text-slate-500 mt-1">Total members</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-purple-500/10 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
                  <Users className="h-6 w-6 text-purple-500" />
                </div>
              </div>
              {/* Team breakdown */}
              <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-3 gap-2">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Crown className="h-3 w-3 text-purple-500" />
                    <span className="text-lg font-bold text-[#0f2744]">{stats?.total_team_leaders || 0}</span>
                  </div>
                  <p className="text-[10px] text-slate-400 uppercase">Leaders</p>
                </div>
                <div className="text-center border-x border-slate-100">
                  <div className="flex items-center justify-center gap-1">
                    <Briefcase className="h-3 w-3 text-cyan-500" />
                    <span className="text-lg font-bold text-[#0f2744]">{stats?.total_sourcing_managers || 0}</span>
                  </div>
                  <p className="text-[10px] text-slate-400 uppercase">Sourcing</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <UserCheck className="h-3 w-3 text-blue-500" />
                    <span className="text-lg font-bold text-[#0f2744]">{stats?.total_agents || 0}</span>
                  </div>
                  <p className="text-[10px] text-slate-400 uppercase">Agents</p>
                </div>
              </div>
              <ArrowUpRight className="absolute bottom-4 right-4 h-4 w-4 text-slate-300 group-hover:text-purple-500 transition-colors" />
            </div>
          )}

          {/* Property Seekers - Only visible to Admin */}
          {isAdmin && (
            <div 
              className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group cursor-pointer"
              onClick={() => navigate("/property-seekers")}
              data-testid="metric-property-seekers"
            >
              <div className="absolute left-0 top-0 h-full w-1 bg-green-500" />
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Property Seekers</p>
                  <p className="text-4xl font-bold text-[#0f2744] mt-2">{stats?.total_users || 0}</p>
                  <p className="text-sm text-slate-500 mt-1">Registered users</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-green-500/10 flex items-center justify-center group-hover:bg-green-500/20 transition-colors">
                  <User className="h-6 w-6 text-green-500" />
                </div>
              </div>
              <ArrowUpRight className="absolute bottom-4 right-4 h-4 w-4 text-slate-300 group-hover:text-green-500 transition-colors" />
            </div>
          )}
        </div>

        {/* Property Carousel */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <PropertyCarousel />
        </div>

        {/* Recent Leads Section */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-[#0f2744]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Recent Leads
              </h2>
              <p className="text-sm text-slate-500 mt-1">Latest inquiries from potential clients</p>
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate("/leads")}
              className="text-[#0f2744] hover:bg-[#0f2744]/5"
              data-testid="view-all-leads-btn"
            >
              View All
              <ArrowUpRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
          <div className="p-6">
            {recentLeads.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No leads yet</p>
                <Button
                  onClick={() => navigate("/leads/new")}
                  className="mt-4 bg-[#0f2744] hover:bg-[#1e3a5f] text-white rounded-lg"
                >
                  Add Your First Lead
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {recentLeads.map((lead) => (
                  <div
                    key={lead.id}
                    data-testid={`lead-item-${lead.id}`}
                    className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-all duration-200 cursor-pointer border border-transparent hover:border-slate-200 hover:shadow-sm group"
                    onClick={() => navigate(`/leads/${lead.id}`)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <p className="font-semibold text-[#0f2744] group-hover:text-[#c9a227] transition-colors">{lead.name}</p>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        lead.status === 'new' ? 'bg-blue-100 text-blue-700' :
                        lead.status === 'contacted' ? 'bg-yellow-100 text-yellow-700' :
                        lead.status === 'qualified' ? 'bg-purple-100 text-purple-700' :
                        lead.status === 'converted' ? 'bg-green-100 text-green-700' :
                        lead.status === 'lost' ? 'bg-red-100 text-red-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>{lead.status}</span>
                    </div>
                    <p className="text-sm text-slate-600 mb-1">{lead.email}</p>
                    <p className="text-sm text-slate-500">{lead.phone}</p>
                    {lead.property_title && (
                      <p className="text-xs text-[#c9a227] mt-3 truncate font-medium">
                        <Building2 className="h-3 w-3 inline mr-1" />
                        {lead.property_title}
                      </p>
                    )}
                    {lead.ai_score && (
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
                        <span className="text-xs text-slate-400 uppercase tracking-wide">AI Score</span>
                        <span className={`text-sm font-bold ${
                          lead.ai_score >= 70 ? 'text-green-600' :
                          lead.ai_score >= 40 ? 'text-yellow-600' : 'text-red-600'
                        }`}>{lead.ai_score.toFixed(0)}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
